/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useCallback, useMemo } from 'react';
import { Container, Typography, Button, Table, useToast } from '@crisellevaldez/speculo-ui';
import { SteppedModal } from '../../components/common/SteppedModal';
import { formatNumber } from '../../utils/format';
import { EditConvertFlowModal } from '../../components/features/flows/EditConvertFlowModal';
import { ViewFlowContactsModal } from '../../components/features/flows/ViewFlowContactsModal';
import { FlowCallRecordsModal } from '../../components/features/flows/FlowCallRecordsModal';
import { PlusCircle, Phone, Copy, Pencil, Trash, Timer } from 'lucide-react';
import { ConfirmationDialog, LoadingDialog } from '../../components/common/dialogs';
import { ConvertFlow } from '../../hooks/useFlowsQuery';
import { useFlowsGQL } from '../../hooks/graphql/flows/useFlowsGQL';
import { OffsetPagination } from '../../components/common/OffsetPagination';
import { TableCard } from '../../components/common/TableCard';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useHasIntegration } from '../../hooks/useHasIntegration';
import { buildTransferLineOptions, formatTransferNumbers } from '../../utils/flows/formatTransferNumbers';
import {
  ConvertFlowSteps,
  IntegrationSetupData,
} from '../../components/features/flows/ConvertFlowSteps';
import { createFlow } from '../../services/flows';
import { generateVoicemail as generateVoicemailAPI } from '../../api/flows';
import { deleteFlow } from '../../api/remiApi';
import { generateLeadEmail } from '../../utils/generateLeadEmail';
import { AddContactsModal } from '../../components/features/flows/AddContactsModal';
import { ConvertTableHeader } from '../../components/features/flows/ConvertTableHeader';
import { FlowDescriptionCard } from '../../components/features/flows/FlowDescriptionCard';
import { useUser } from '../../hooks/useUser';

const ConvertFlowPage: React.FC = () => {
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();
  const { users } = useOrganizationUsers();
  const { user: userData, isAdmin } = useUser();
  const hasIntegration = useHasIntegration();

  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [showViewContactsModal, setShowViewContactsModal] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [createdFlow, setCreatedFlow] = useState<{ id: string; name: string } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<ConvertFlow | null>(null);
  const [flowToEdit, setFlowToEdit] = useState<ConvertFlow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const [newFlowStep, setNewFlowStep] = useState(0);

  // Flow basic info
  const [flowName, setFlowName] = useState('');
  const [flowSource, setFlowSource] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [transferLines, setTransferLines] = useState<string[]>([]);
  const [leadEmail, setLeadEmail] = useState('');
  const [useRoundRobin, setUseRoundRobin] = useState(false);

  // Integration Setup (for non-FUB users)
  const [integrationSetup, setIntegrationSetup] = useState<IntegrationSetupData>({
    contactType: '',
    crmSourceType: '',
    crmSources: [],
    useTags: false,
    tagsAction: '',
    tags: [],
    useAssignment: false,
    assignmentAction: '',
    assignedAgents: [],
    additionalNotes: '',
  });

  // Orgs with an integration: 4 steps (0-3), no Integration Setup.
  // Orgs with no integration: 5 steps (0-4) with Integration Setup at step 3.
  const totalSteps = hasIntegration ? 4 : 5;
  const summaryStepIndex = hasIntegration ? 3 : 4;
  const integrationStepIndex = 3;

  // Generate lead email only once when flow name is first set
  React.useEffect(() => {
    if (organization?.id && flowName && !leadEmail) {
      setLeadEmail(generateLeadEmail(organization.id, flowName));
    }
  }, [organization?.id, flowName, leadEmail]);

  // Basic info errors
  const [flowNameError, setFlowNameError] = useState('');
  const [flowSourceError, setFlowSourceError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<{ field?: string; direction: 'asc' | 'desc' }>({
    field: 'createdAt',
    direction: 'desc',
  });

  const {
    data,
    totalCount,
    currentPage,
    onNextPage,
    onPrevPage,
    onPageChange,
    isLoading: loading,
    error: queryError,
    refetch: originalRefetch,
  } = useFlowsGQL({
    flowType: 'Convert',
    organizationId: organization?.id || '',
    pageSize,
    sortField: sortConfig.field,
    sortDirection: sortConfig.direction,
    searchQuery,
  });

  const flows = (data?.pages[0]?.flows || []) as ConvertFlow[];

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const error = queryError ? (queryError as Error).message : null;

  // Action Handlers
  const [showCallRecordsModal, setShowCallRecordsModal] = useState(false);
  const [selectedCallFlow, setSelectedCallFlow] = useState<ConvertFlow | null>(null);

  const handleViewCallRecords = useCallback((flow: ConvertFlow) => {
    setSelectedCallFlow(flow);
    setShowCallRecordsModal(true);
  }, []);

  const handleViewContacts = useCallback((flow: ConvertFlow) => {
    setSelectedFlowId(flow.id);
    setShowViewContactsModal(true);
  }, []);

  const handleDeleteClick = useCallback((flow: ConvertFlow) => {
    setFlowToDelete(flow);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!flowToDelete) return;

    setIsDeleting(true);
    try {
      await deleteFlow(organization?.id || '', flowToDelete.id);
      await originalRefetch();
      showToast({
        message: 'Flow archived successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error archiving flow:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to archive flow',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFlowToDelete(null);
    }
  }, [flowToDelete, originalRefetch, showToast]);

  // Memoize columns with userData?.role as dependency to fix stale closure issue
  const isSuperAdmin = userData?.role === 'super_admin';
  const tableColumns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: '300px',
        resizable: true,
        sortable: true,
        isPinned: true,
        pinPosition: 'left' as const,
        cell: (row: ConvertFlow) => row.name,
      },
      {
        key: 'lead_source',
        header: 'Lead Source',
        width: '200px',
        sortable: true,
        cell: (row: ConvertFlow) => row.lead_source || '-',
      },
      {
        key: 'lead_email',
        header: 'Lead Email',
        width: '250px',
        cell: (row: ConvertFlow) => row.lead_email || '-',
      },
      {
        key: 'contacts',
        header: 'Contacts',
        width: '120px',
        isCentered: true,
        cell: (row: ConvertFlow) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewContacts(row)}
            className="px-3 py-1 text-xs"
          >
            {formatNumber(row.contacts_count ?? 0)}
          </Button>
        ),
      },
      {
        key: 'calls',
        header: 'Calls',
        width: '120px',
        isCentered: true,
        cell: (row: ConvertFlow) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewCallRecords(row)}
            className="px-3 py-1 text-xs"
          >
            {formatNumber(row.calls_count ?? 0)}
          </Button>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: '160px',
        isCentered: true,
        isPinned: true,
        pinPosition: 'right' as const,
        cell: (row: ConvertFlow) => (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(row.lead_email);
                showToast({
                  message: 'Email copied to clipboard',
                  variant: 'success',
                });
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10"
              title="Copy Email"
            >
              <Copy className="w-3.5 h-3.5 text-gray-800 dark:text-gray-200" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFlowToEdit(row);
                setShowEditModal(true);
              }}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/10"
              title="Edit"
            >
              <Pencil className="w-3.5 h-3.5 text-gray-800 dark:text-gray-200" />
            </Button>
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteClick(row)}
                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 border-red-300 dark:border-red-700/30 hover:border-red-500"
                title="Delete"
              >
                <Trash className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [isSuperAdmin, handleViewContacts, handleViewCallRecords, handleDeleteClick, showToast]
  );

  return (
    <Container className="px-4 lg:px-5 xl:pl-20 xl:pr-5 pb-6">
      <div className="flex flex-col space-y-6 lg:space-y-10">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Title and Actions */}
          <div>
            <div className="flex flex-col lg:flex-row justify-between lg:justify-start items-start lg:items-center gap-3 lg:gap-20">
              <div>
                <Typography variant="h3" as="h1" className="!text-xl font-bold">
                  Convert Flows
                </Typography>
                <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                  {formatNumber(totalCount)} {totalCount === 1 ? 'record' : 'records'}
                </Typography>
              </div>
              {isAdmin ? (
                (!hasIntegration || isSuperAdmin) && (
                  <Button
                    variant="primary"
                    size="md"
                    className="flex items-center justify-center gap-2 w-full sm:w-auto"
                    onClick={() => setShowNewFlowModal(true)}
                  >
                    <PlusCircle size={16} />
                    New Convert Flow
                  </Button>
                )
              ) : (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  <Button
                    variant="primary"
                    size="md"
                    className="flex items-center justify-center gap-2 w-full sm:w-auto"
                    disabled
                  >
                    <PlusCircle size={16} />
                    New Convert Flow
                  </Button>
                  <p className="text-xs text-muted-foreground self-center">
                    For launching new flows, contact the admin on your account.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <FlowDescriptionCard
            flowType="convert"
            description={[
              'Convert Flows are the front door to your Follow-Up Flywheel: every lead gets contacted instantly, then Remi nurtures them forever with full context.',
              'What they called about, which homes they liked, what value they wanted—so every follow-up feels personal, timely, and impossible to ignore. More sources in, more smart conversations out, more deals closed.',
            ]}
            icon={Timer}
            tags={['Google PPC', 'FB Leads', 'Zillow (non live transfers)', 'Realtor.com']}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          <ConvertTableHeader
            searchQuery={searchQuery}
            onSearchChange={handleSearch}
            onRefresh={async () => {
              try {
                setSearchQuery('');
                await originalRefetch();
              } catch (error) {
                console.error('Error refreshing flows:', error);
                showToast({
                  message: 'Failed to refresh flows',
                  variant: 'error',
                });
              }
            }}
            isLoading={loading}
            totalFlows={totalCount}
          />

          <TableCard
            className="h-[60vh] sm:h-[calc(100vh-220px)]"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                onPageChange={onPageChange}
                onPageSizeChange={(size) => { setPageSize(size); }}
                isLoading={loading}
                label="flows"
              />
            }
          >
            <Table<ConvertFlow>
              key={`convert-table-${isSuperAdmin}`}
              columns={tableColumns}
              data={flows}
              keyExtractor={(item) => item.id}
              sortable={true}
              onSort={handleSort}
              loading={loading}
              emptyStateText="No convert flows available"
            />
          </TableCard>

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showDeleteModal}
            onClose={() => {
              if (!isDeleting) {
                setShowDeleteModal(false);
                setFlowToDelete(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            title="Delete Flow"
            message={
              flowToDelete
                ? `Are you sure you want to delete the flow "${flowToDelete.name}"?`
                : 'Are you sure you want to delete this flow?'
            }
            confirmLabel="Delete"
            isLoading={isDeleting}
            variant="danger"
          />

          {/* Edit Flow Modal */}
          <EditConvertFlowModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setFlowToEdit(null);
            }}
            flow={flowToEdit}
          />

          {/* Call Records Modal */}
          <FlowCallRecordsModal
            isOpen={showCallRecordsModal}
            onClose={() => {
              setShowCallRecordsModal(false);
              setSelectedCallFlow(null);
            }}
            flowId={selectedCallFlow?.id || ''}
            flowName={selectedCallFlow?.name || ''}
            orgTimezone={organization?.timezone}
            organization={organization || undefined}
          />

          {/* View Contacts Modal */}
          <ViewFlowContactsModal
            isOpen={showViewContactsModal}
            onClose={() => {
              setShowViewContactsModal(false);
              setSelectedFlowId('');
            }}
            flowId={selectedFlowId}
          />

          {/* New Flow Stepped Modal */}
          <SteppedModal
            isOpen={showNewFlowModal}
            onClose={() => {
              setShowNewFlowModal(false);
              // Reset errors and step
              setFlowNameError('');
              setFlowSourceError('');
              setDescriptionError('');
              setNewFlowStep(0);
              // Reset lead email to ensure fresh generation on next open
              setLeadEmail('');
              // Reset integration setup
              setIntegrationSetup({
                contactType: '',
                crmSourceType: '',
                crmSources: [],
                useTags: false,
                tagsAction: '',
                tags: [],
                useAssignment: false,
                assignmentAction: '',
                assignedAgents: [],
                additionalNotes: '',
              });
            }}
            headerTitle="New Convert Flow"
            title={
              newFlowStep === 0
                ? 'What type of Convert flow is this?'
                : newFlowStep === 1
                  ? 'Describe your Convert flow'
                  : newFlowStep === 2
                    ? 'Select transfer line'
                    : !hasIntegration && newFlowStep === 3
                      ? 'Integration setup'
                      : 'Convert flow summary'
            }
            currentStep={newFlowStep}
            onNext={() => setNewFlowStep((prev) => prev + 1)}
            disableNext={
              (newFlowStep === 0 && !flowName.trim()) ||
              (newFlowStep === 1 && !customDescription.trim()) ||
              (newFlowStep === 2 && (!transferLines || transferLines.length === 0)) ||
              (!hasIntegration &&
                newFlowStep === 3 &&
                (!integrationSetup.contactType ||
                  !integrationSetup.crmSourceType ||
                  (integrationSetup.crmSourceType === 'specific' &&
                    integrationSetup.crmSources.length === 0)))
            }
            onPrevious={() => setNewFlowStep((prev) => prev - 1)}
            onReset={() => {
              // Reset basic info
              setFlowName('');
              setFlowSource('');
              setSelectedDescription('');
              setCustomDescription('');
              setTransferLines([]);
              setLeadEmail('');
              setUseRoundRobin(false);

              // Reset integration setup
              setIntegrationSetup({
                contactType: '',
                crmSourceType: '',
                crmSources: [],
                useTags: false,
                tagsAction: '',
                tags: [],
                useAssignment: false,
                assignmentAction: '',
                assignedAgents: [],
                additionalNotes: '',
              });

              // Reset all errors
              setFlowNameError('');
              setFlowSourceError('');
              setDescriptionError('');

              // Reset step
              setNewFlowStep(0);
            }}
            onComplete={async () => {
              setIsCreatingFlow(true);
              try {
                // Get selected transfer line options and format them
                const selectedTransferOptions = buildTransferLineOptions(users).filter((opt) =>
                  transferLines.includes(opt.value)
                );
                const formattedNumbers = formatTransferNumbers(selectedTransferOptions);

                // Get first transfer number for call_settings
                const transferPhoneNumber = selectedTransferOptions[0]?.value || null;

                // Generate voicemail content first so we can include it in
                // the createFlow payload — avoids a follow-up PUT that would
                // null out other fields.
                let voicemailContent: string | undefined;
                try {
                  const vmResponse = await generateVoicemailAPI(customDescription);
                  voicemailContent = vmResponse.voicemail;
                } catch (vmError) {
                  console.warn('Voicemail generation failed:', vmError);
                }

                // Create flow
                const createResult = await createFlow({
                  name: flowName,
                  description: customDescription,
                  description_id: selectedDescription,
                  flow_type: 'Convert',
                  sub_type: '',
                  timezone: organization?.timezone || 'UTC',
                  country_code: 'US',
                  transfer_numbers: formattedNumbers,
                  lead_email: leadEmail,
                  lead_source: flowSource || '',
                  useRoundRobin: useRoundRobin,
                  voicemail_content: voicemailContent,
                  // Include integration setup data for orgs with no existing integration
                  ...(!hasIntegration && integrationSetup.contactType
                    ? {
                        integrationSetup: {
                          ...integrationSetup,
                          status: 'pending',
                          submittedBy: userData?.email || 'Unknown',
                          submittedByName: userData?.displayName || '',
                          submittedAt: new Date().toISOString(),
                          organizationName: organization?.org_name || '',
                        },
                      }
                    : {}),
                });

                // Refresh flows list and show add contacts modal
                await originalRefetch();
                if (createResult?.integrationSetupFailed) {
                  showToast({
                    message:
                      'Flow created, but there was a problem submitting your integration request. Please contact support.',
                    variant: 'warning',
                  });
                } else {
                  showToast({
                    message: hasIntegration
                      ? 'Flow created successfully.'
                      : 'Flow created successfully. Integration request submitted!',
                    variant: 'success',
                  });
                }
                // Reset all form values
                setFlowName('');
                setFlowSource('');
                setSelectedDescription('');
                setCustomDescription('');
                setTransferLines([]);
                setLeadEmail('');
                setIntegrationSetup({
                  contactType: '',
                  crmSourceType: '',
                  crmSources: [],
                  useTags: false,
                  tagsAction: '',
                  tags: [],
                  useAssignment: false,
                  assignmentAction: '',
                  assignedAgents: [],
                  additionalNotes: '',
                });
                // Reset step and close flow modal
                setNewFlowStep(0);
                setShowNewFlowModal(false);
              } catch (error) {
                console.error('Error creating flow:', error);
                showToast({
                  message: error instanceof Error ? error.message : 'Failed to create flow',
                  variant: 'error',
                });
              } finally {
                setIsCreatingFlow(false);
              }
            }}
            size="xl"
          >
            {Array.from({ length: totalSteps }, (_, stepIndex) => (
              <div key={stepIndex}>
                <ConvertFlowSteps
                  step={stepIndex}
                  // Flow basic info
                  flowName={flowName}
                  flowSource={flowSource}
                  selectedDescription={selectedDescription}
                  customDescription={customDescription}
                  transferLines={transferLines}
                  leadEmail={leadEmail}
                  // Errors
                  flowNameError={flowNameError}
                  flowSourceError={flowSourceError}
                  descriptionError={descriptionError}
                  // Basic info handlers
                  onFlowNameChange={(value) => {
                    setFlowName(value);
                    if (flowNameError) setFlowNameError('');
                  }}
                  onFlowSourceChange={(value) => {
                    setFlowSource(value);
                    if (flowSourceError) setFlowSourceError('');
                  }}
                  onSelectedDescriptionChange={setSelectedDescription}
                  onCustomDescriptionChange={(value) => {
                    setCustomDescription(value);
                    if (descriptionError) setDescriptionError('');
                  }}
                  onTransferLinesChange={setTransferLines}
                  useRoundRobin={useRoundRobin}
                  onUseRoundRobinChange={setUseRoundRobin}
                  // Integration Setup (for orgs with no existing integration)
                  hasIntegration={hasIntegration}
                  integrationSetup={integrationSetup}
                  onIntegrationSetupChange={setIntegrationSetup}
                />
              </div>
            ))}
          </SteppedModal>

          {/* Loading Dialog */}
          <LoadingDialog isOpen={isCreatingFlow} message="Flow is building..." />

          {/* Add Contacts Modal */}
          <AddContactsModal
            isOpen={showAddContactsModal}
            onClose={() => {
              setShowAddContactsModal(false);
              setCreatedFlow(null);
            }}
            flowName={createdFlow?.name ?? ''}
            flowId={createdFlow?.id ?? ''}
          />
        </div>
      </div>
    </Container>
  );
};

export default ConvertFlowPage;
